import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Anchor, ShieldCheck, Sparkles, Cookie, ExternalLink, Loader2 } from 'lucide-react';

/**
 * CGUReAcceptModal — blocks the first authenticated action until the user
 * checks "J'accepte la nouvelle version des CGU" and posts /auth/accept-cgu.
 *
 * Mounted globally in App.js. Renders only when a user is signed-in and
 * `cgu_up_to_date === false`.
 */
const CGUReAcceptModal = () => {
  const { user, api, refreshUser } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!user) return null;
  if (user.cgu_up_to_date === true) return null;

  const handleAccept = async () => {
    if (!accepted) return;
    setSubmitting(true);
    try {
      await api.post('/auth/accept-cgu');
      await refreshUser();
      toast.success('Merci — nouvelles CGU acceptées');
    } catch (e) {
      toast.error("Impossible d'enregistrer votre acceptation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 37, 64, 0.72)', backdropFilter: 'blur(4px)' }}
      data-testid="cgu-reaccept-modal"
    >
      <div
        className="max-w-lg w-full rounded-2xl p-6 md:p-8 shadow-2xl"
        style={{ background: 'var(--paper, #FAF7F1)', color: 'var(--ink, #0F2540)', border: '1px solid rgba(201,168,106,0.25)' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(201,168,106,0.18)' }}
          >
            <Anchor className="w-6 h-6" style={{ color: 'var(--gold, #C9A86A)' }} />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold leading-tight">Mise à jour des CGU</h2>
            <p className="text-xs text-muted-foreground">
              Version {user.current_cgu_version || '1.1'} — action requise pour continuer
            </p>
          </div>
        </div>

        <p className="text-sm mb-4 leading-relaxed">
          Nous mettons à jour nos Conditions Générales d'Utilisation pour vous informer explicitement
          des changements suivants :
        </p>

        <ul className="space-y-3 mb-5">
          <li className="flex items-start gap-3">
            <Sparkles className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: 'var(--gold, #C9A86A)' }} />
            <div>
              <p className="text-sm font-semibold">Article 50 du Règlement UE 2024/1689 (AI Act)</p>
              <p className="text-xs text-muted-foreground">
                Les contenus produits ou substantiellement modifiés par une IA (résumés, traductions)
                sont désormais signalés par un marqueur explicite dans l'interface.
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <ShieldCheck className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: 'var(--gold, #C9A86A)' }} />
            <div>
              <p className="text-sm font-semibold">Connexion via Google (Emergent OAuth)</p>
              <p className="text-xs text-muted-foreground">
                Vous pouvez désormais vous connecter avec Google. Aucun compte n'est créé automatiquement :
                votre email Google doit correspondre à l'email de votre accompagnement.
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <Cookie className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: 'var(--gold, #C9A86A)' }} />
            <div>
              <p className="text-sm font-semibold">Sous-traitants</p>
              <p className="text-xs text-muted-foreground">
                Emergent (hébergement), Anthropic (Claude — traductions), Resend (emails
                transactionnels), Google (OAuth uniquement), Calendly &amp; ThriveCart (webhooks
                d'inscription). Aucune donnée n'est vendue à des tiers.
              </p>
            </div>
          </li>
        </ul>

        <a
          href="/cgu.html"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs mb-4 hover:underline"
          style={{ color: 'var(--gold, #C9A86A)' }}
          data-testid="cgu-full-link"
        >
          <ExternalLink className="w-3 h-3" />
          Lire la version intégrale des CGU
        </a>

        <label
          className="flex items-start gap-3 p-3 rounded-lg cursor-pointer"
          style={{ background: 'rgba(15,37,64,0.04)' }}
        >
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-current cursor-pointer"
            style={{ accentColor: 'var(--gold, #C9A86A)' }}
            data-testid="cgu-accept-checkbox"
          />
          <span className="text-sm">J'accepte la nouvelle version des CGU</span>
        </label>

        <button
          onClick={handleAccept}
          disabled={!accepted || submitting}
          className="mt-4 w-full px-5 py-3 rounded-lg font-semibold transition-transform disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
          style={{ background: 'var(--gold, #C9A86A)', color: 'var(--paper, #FAF7F1)' }}
          data-testid="cgu-accept-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Continuer vers L'Équipage"}
        </button>
      </div>
    </div>
  );
};

export default CGUReAcceptModal;
